import { useEffect, useRef } from 'react';
import { usePrivate } from '@/components/private';
import { useLaunchDarklyUpdate } from '@/providers/LaunchDarklyProvider';

/**
 * Hook to automatically update LaunchDarkly with user information
 * This should be used in components that have access to user data
 */
export const useLaunchDarklyUser = () => {
  const { updateUser } = useLaunchDarklyUpdate();
  const { userId, userEmail, userGuid, selectedOrganisationId, userProfile } =
    usePrivate();

  // Use ref to track previous values and prevent unnecessary updates
  const prevUserDataRef = useRef<{
    userId?: number;
    userEmail?: string;
    userGuid?: string;
    selectedOrganisationId?: number;
    userProfile?: any;
  }>({});

  useEffect(() => {
    if (userId || userEmail) {
      const userName =
        userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : userProfile?.firstName || undefined;

      const userType = 'standard'; // Default user type since UserProfileDto doesn't have userType

      const currentUserData = {
        userId,
        userEmail,
        userName,
        userGuid,
        organizationId: selectedOrganisationId,
        userType,
      };

      const prevUserData = prevUserDataRef.current;

      // Check if user data has actually changed
      const hasChanged =
        prevUserData.userId !== userId ||
        prevUserData.userEmail !== userEmail ||
        prevUserData.userGuid !== userGuid ||
        prevUserData.selectedOrganisationId !== selectedOrganisationId ||
        prevUserData.userProfile?.firstName !== userProfile?.firstName ||
        prevUserData.userProfile?.lastName !== userProfile?.lastName;

      if (hasChanged) {
        updateUser(currentUserData);

        console.log('[LaunchDarkly] User context updated:', currentUserData);

        // Update the ref with current values
        prevUserDataRef.current = {
          userId,
          userEmail,
          userGuid,
          selectedOrganisationId,
          userProfile,
        };
      }
    }
  }, [
    updateUser,
    userId,
    userEmail,
    userGuid,
    selectedOrganisationId,
    userProfile,
  ]);
};
